import { cva } from 'class-variance-authority';
import { uiMarker, uiMarkerInternal, uiMarkerLayout } from './shared';

export const prospectBoardRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'relay-prospect-board flex h-full min-h-0 w-full overflow-hidden bg-background text-foreground [&_svg]:stroke-[1.5]',
]);
export const prospectRailRecipe = cva([
  uiMarkerInternal,
  'hidden w-[280px] shrink-0 flex-col overflow-y-auto border-r border-border bg-canvas px-3 py-4 min-[1280px]:flex min-[1280px]:w-[280px]',
]);
export const prospectListRecipe = cva([
  uiMarkerInternal,
  'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-border min-[1280px]:w-[400px] min-[1280px]:flex-none',
]);
export const prospectDetailRecipe = cva([
  uiMarkerInternal,
  'hidden min-h-0 min-w-0 flex-1 flex-col overflow-y-auto min-[768px]:flex',
]);
export const prospectListVisibilityRecipe = cva([uiMarkerInternal], {
  variants: {
    mobileHidden: { true: 'hidden min-[768px]:flex', false: 'flex' },
  },
  defaultVariants: { mobileHidden: false },
});
export const prospectDetailVisibilityRecipe = cva([uiMarkerInternal], {
  variants: {
    mobileVisible: { true: 'flex', false: 'hidden min-[768px]:flex' },
  },
  defaultVariants: { mobileVisible: false },
});
export const prospectNavRecipe = cva(
  [
    uiMarker,
    'flex min-h-9 w-full items-center gap-2 rounded-md px-2 text-left text-body-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
  ],
  {
    variants: { selected: { true: 'bg-muted text-foreground', false: '' } },
    defaultVariants: { selected: false },
  }
);
export const prospectActivityRecipe = cva(
  [
    uiMarker,
    'relative mx-2 flex min-h-10 items-center gap-3 rounded-md px-3 text-left text-muted-foreground hover:bg-muted/60 before:h-5 before:w-1 before:shrink-0 before:rounded-full before:bg-border',
  ],
  {
    variants: {
      selected: {
        true: 'bg-muted text-foreground before:bg-primary',
        false: '',
      },
    },
    defaultVariants: { selected: false },
  }
);
export const prospectTaskRecipe = cva([
  uiMarker,
  'flex min-h-10 items-center justify-between gap-3 px-1',
]);
export const prospectPropertyRecipe = cva(
  [
    uiMarker,
    'grid min-h-10 grid-cols-[20px_112px_minmax(0,1fr)] items-center gap-2 py-2',
  ],
  {
    variants: {
      mobileStack: {
        true: 'max-[639px]:grid-cols-[20px_minmax(0,1fr)]',
        false: '',
      },
    },
    defaultVariants: { mobileStack: false },
  }
);
export const prospectPropertyValueRecipe = cva(
  [uiMarkerInternal, 'min-w-0 break-words'],
  {
    variants: {
      mobileStack: {
        true: 'max-[639px]:col-start-2',
        false: '',
      },
    },
    defaultVariants: { mobileStack: false },
  }
);

/**
 * The bar across the top of a pane: the view switcher and search on a narrow
 * screen, or a record's name and its actions. `railHidden` is for a bar that
 * stands in for the rail, so it goes once the rail shows at 1280.
 */
export const prospectBarRecipe = cva(
  [
    uiMarker,
    'flex min-h-12 shrink-0 items-center gap-2 border-b border-border px-4 py-2',
  ],
  {
    variants: {
      sticky: { true: 'sticky top-0 z-10 bg-background', false: '' },
      railHidden: { true: 'min-[1280px]:hidden', false: '' },
    },
    defaultVariants: { sticky: false, railHidden: false },
  }
);

/** A block of the list or detail pane, inset by what it holds. */
export const prospectPaneSectionRecipe = cva([uiMarker], {
  variants: {
    inset: {
      /** The day heading at the top of the list. */
      title: 'px-4 pb-3 pt-6',
      /** A small label row over a list, such as Calls and its period filter. */
      label: 'px-4 pb-2 pt-3',
      /** A group under its own heading, such as Tasks. */
      group: 'px-4 pb-4 pt-5',
      /** The review banner, inset to line up with the activity rows. */
      banner: 'px-3 pb-3',
      /** A message in the list: loading, empty, an error. */
      body: 'p-4',
      /** A message in the detail pane. */
      detail: 'p-6',
    },
  },
  defaultVariants: { inset: 'body' },
});

/** The scrolling body of an open prospect, under its bar. */
export const prospectRecordBodyRecipe = cva([
  uiMarker,
  'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 pb-8 pt-5 md:px-8',
]);

/** The property rows of an open prospect, ruled off from the tabs below. */
export const prospectPropertiesRecipe = cva([
  uiMarker,
  'border-b border-border pb-5',
]);

export const prospectReviewRecipe = cva([
  uiMarker,
  'flex min-h-12 w-full items-center gap-3 rounded-md border border-border px-3 text-left text-muted-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
]);
export const prospectPeriodRecipe = cva([
  uiMarker,
  'cursor-pointer border-0 bg-transparent py-1 pl-2 pr-1 font-sans text-body-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
]);

/**
 * A call summary in the open prospect: one point per line, held to a
 * reading width so a long summary wraps at a comfortable measure instead
 * of running the full width of the pane.
 */
export const prospectSummaryRecipe = cva([uiMarker, 'max-w-prose']);

/** One earlier call in the open prospect, ruled off from the next. */
export const prospectCallEntryRecipe = cva([
  uiMarker,
  'flex flex-col items-start gap-3 py-5 first:pt-0 last:pb-0',
]);

/**
 * The board on its own page, outside the dashboard: a slim bar across the
 * top and the board filling the rest. `fill="screen"` is the real page;
 * `fill="parent"` sizes it to a box, for the gallery.
 */
export const prospectFrameRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex w-full flex-col overflow-hidden bg-background text-foreground',
  ],
  {
    variants: { fill: { screen: 'h-dvh', parent: 'h-full' } },
    defaultVariants: { fill: 'screen' },
  }
);
export const prospectFrameBarRecipe = cva([
  uiMarkerInternal,
  'flex min-h-12 shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2',
]);
export const prospectFrameBodyRecipe = cva([
  uiMarkerInternal,
  'flex min-h-0 flex-1 flex-col',
]);
